import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { type ApiResponse, useMemoizedArrayData } from 'src/hooks/api/api';
import {
  type FunnelConversation,
  type FunnelConversationListResponse,
  type FunnelConversationSearchResponse,
  type FunnelConversationSearchBodyParams,
  CrmFunnelsConversationsService,
  type OptionalTenantFunnelConversationListQueryParams as FunnelConversationListQueryParams,
  type FunnelConversationDocument,
} from 'src/api/generated';
import { reconcileCallPreview } from 'src/modules/shared/communications/utils/cache/funnel-conversations/call-preview.util';
import { reconcileDeletedMessagePreview } from 'src/modules/shared/communications/utils/cache/funnel-conversations/deleted-message-preview.util';
import { SEC_IN_MS } from 'src/utils/date';
import { conversationQueryOptions } from 'src/modules/shared/communications/utils/api/conversation-query.util';
import { awaitCancelableRequest } from 'src/modules/shared/utils/api/cancelable-request.util';
import { reconcileConversationDocuments } from 'src/modules/shared/communications/utils/cache/funnel-conversations/recent-conversation-events.util';
import { recoverCanonicalConversation } from 'src/modules/shared/communications/services/canonical-conversation-recovery.service';

interface UseFunnelConversationsParams
  extends Omit<FunnelConversationListQueryParams, 'tenantId'> {}

interface UseFunnelConversationsResponse extends ApiResponse {
  conversations: FunnelConversation[];
  fetchNextPage: () => Promise<unknown>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

export const useFunnelConversations = (
  params: UseFunnelConversationsParams,
  enabled = true
): UseFunnelConversationsResponse => {
  const fetchConversations = async ({
    pageParam,
  }: {
    pageParam: string | undefined;
  }): Promise<FunnelConversationListResponse> => {
    const { filterBy, searchBy, sortBy, page, pageSize } = params;

    return await CrmFunnelsConversationsService.getFunnelConversations(
      filterBy,
      searchBy,
      sortBy,
      page,
      pageSize,
      pageParam
    );
  };

  const getNextPageParam = (lastPage: FunnelConversationListResponse) =>
    lastPage.nextPageToken;

  const {
    data,
    error,
    isLoading: loading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['crm/funnel-conversations', params],
    queryFn: fetchConversations,
    getNextPageParam,
    initialPageParam: undefined,
    enabled,
  });

  const conversations = useMemoizedArrayData<FunnelConversation>(
    data?.pages.flatMap((page) => page.items) ?? []
  );

  return {
    loading,
    error,
    conversations,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  };
};

interface UseFunnelConversationParams {
  id: number;
}

interface UseFunnelConversationResponse extends ApiResponse {
  conversation: FunnelConversation | null;
}

export const useFunnelConversation = (
  { id }: UseFunnelConversationParams,
  enabled = true
): UseFunnelConversationResponse => {
  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    ...conversationQueryOptions(id),
    enabled,
  });

  return {
    loading,
    error,
    conversation: data ?? null,
    refetch,
  };
};

interface UseSearchFunnelConversationsParams
  extends FunnelConversationSearchBodyParams {}

interface UseSearchFunnelConversationsResponse extends ApiResponse {
  conversations: FunnelConversationDocument[];
  fetchNextPage: () => Promise<unknown>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  totalCount?: number;
}

export function useFunnelConversationDocuments(
  params: UseSearchFunnelConversationsParams,
  enabled = true
): UseSearchFunnelConversationsResponse {
  const queryClient = useQueryClient();
  const fetchConversations = async ({
    pageParam,
    signal,
  }: {
    pageParam: string | undefined;
    signal: AbortSignal;
  }): Promise<FunnelConversationSearchResponse> => {
    const response = await awaitCancelableRequest(
      CrmFunnelsConversationsService.searchFunnelConversations({
        ...params,
        pageToken: pageParam,
      }),
      signal
    );
    signal.throwIfAborted();
    return {
      ...response,
      items: reconcileConversationDocuments(
        queryClient,
        ['crm/documents/funnel-conversations', params],
        response.items,
        (id, tenantId, revision) => {
          void recoverCanonicalConversation(
            queryClient,
            id,
            tenantId,
            revision
          );
        }
      ).map((item) =>
        reconcileCallPreview(
          queryClient,
          reconcileDeletedMessagePreview(queryClient, item)
        )
      ),
    };
  };

  const getNextPageParam = (lastPage: FunnelConversationSearchResponse) =>
    lastPage.nextPageToken;

  const {
    data,
    error,
    isLoading: loading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['crm/documents/funnel-conversations', params],
    queryFn: fetchConversations,
    getNextPageParam,
    initialPageParam: undefined,
    enabled,
    refetchInterval: SEC_IN_MS * 60, // Increased from 30s to 60s
    staleTime: SEC_IN_MS * 45, // Data considered fresh for 45s
  });

  const conversations = useMemoizedArrayData<FunnelConversationDocument>(
    data?.pages.flatMap((page) => page.items) ?? []
  );

  return {
    loading,
    error,
    conversations,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    totalCount: data?.pages[0]?.totalCount,
  };
}
